import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Phone, Mail, Loader2, UserCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const PROT_LABELS = {
  cire: "Cire", protection_12_mois: "Protection 12 mois", ceramique_carrosserie: "Céramique carrosserie",
  ceramique_vitres: "Céramique vitres", traitement_cuir: "Traitement cuir", traitement_textile: "Traitement textile",
  protection_capote: "Protection capote", autre: "Autre",
};
const STATUS = { active: ["Active", "text-emerald-700 bg-emerald-50 border-emerald-200"], bientot: ["Bientôt à renouveler", "text-amber-700 bg-amber-50 border-amber-200"], expiree: ["Expirée", "text-red-700 bg-red-50 border-red-200"] };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");

export default function Relances() {
  const [period, setPeriod] = useState(6);
  const [clients, setClients] = useState([]);
  const [prots, setProts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        api.get("/clients-follow-up", { params: { months: period } }),
        api.get("/protections/upcoming", { params: { days: 60 } }),
      ]);
      setClients(c.data); setProts(p.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="Clients à relancer" subtitle="Relance commerciale et protections à renouveler" icon={UserCheck} />

      <div className="flex flex-wrap gap-2 mb-5" data-testid="relance-periods">
        {[[3, "3 mois"], [6, "6 mois"], [12, "12 mois"]].map(([v, l]) => (
          <button key={v} data-testid={`relance-period-${v}`} onClick={() => setPeriod(v)}
            className={`px-4 py-2 rounded-lg border text-sm transition ${period === v ? "bg-emerald-500 text-black border-emerald-500 font-semibold" : "bg-white text-slate-500 border-slate-200"}`}>
            Sans prestation depuis {l}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div> : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="font-heading text-base font-semibold text-slate-900 mb-3">Clients ({clients.length})</h3>
            {clients.length ? (
              <div className="space-y-2" data-testid="relance-clients-list">
                {clients.map((c) => (
                  <div key={c.client_id} data-testid="relance-client-row" className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <button onClick={() => navigate(`/clients/${c.client_id}`)} className="min-w-0 text-left">
                      <div className="text-sm text-slate-900 font-medium truncate">{c.client_name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {c.never ? "Aucune prestation enregistrée" : `Dernier RDV : ${fmt(c.last_appointment_date)}`}
                        {c.vehicle_name ? ` · ${c.vehicle_name}` : ""}
                        {c.expiring_protections > 0 ? ` · ${c.expiring_protections} protection(s) à renouveler` : ""}
                      </div>
                    </button>
                    <div className="flex gap-1.5 shrink-0">
                      {c.phone && <a href={`tel:${c.phone}`} data-testid="relance-call" className="p-2 rounded-lg bg-white border border-slate-200 text-emerald-600 hover:bg-emerald-50"><Phone className="w-4 h-4" /></a>}
                      {c.email && <a href={`mailto:${c.email}`} data-testid="relance-email" className="p-2 rounded-lg bg-white border border-slate-200 text-sky-600 hover:bg-sky-50"><Mail className="w-4 h-4" /></a>}
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-500">Aucun client à relancer sur cette période.</p>}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="font-heading text-base font-semibold text-slate-900 mb-3 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-600" /> Protections à renouveler</h3>
            {prots.length ? (
              <div className="space-y-2" data-testid="relance-protections-list">
                {prots.map((p) => {
                  const [lbl, cls] = STATUS[p.status] || STATUS.active;
                  return (
                    <button key={p.id} onClick={() => p.client_id && navigate(`/clients/${p.client_id}`)} className="w-full text-left bg-slate-50 border border-slate-200 rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm text-slate-900 truncate">{PROT_LABELS[p.type] || p.type}</div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${cls}`}>{lbl}</span>
                      </div>
                      <div className="text-xs text-slate-500 truncate">{p.client_name || ""}{p.vehicle_name ? ` · ${p.vehicle_name}` : ""} · échéance {fmt(p.expiry_date)}</div>
                    </button>
                  );
                })}
              </div>
            ) : <p className="text-sm text-slate-500">Aucune protection à renouveler prochainement.</p>}
          </div>
        </div>
      )}
      <ServiceFollowups />
    </div>
  );
}

const FU = { planifiee: ["Planifiée", "text-slate-700 bg-slate-50 border-slate-200"], a_envoyer: ["À envoyer", "text-amber-700 bg-amber-50 border-amber-200"], envoyee: ["Envoyée", "text-emerald-700 bg-emerald-50 border-emerald-200"], sans_email: ["Sans e-mail", "text-red-700 bg-red-50 border-red-200"], expiree: ["Expirée", "text-slate-500 bg-slate-50 border-slate-200"] };

function ServiceFollowups() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/followups/upcoming", { params: { days: 120 } }).then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const send = async (f) => {
    try { await api.post("/followups/send", { appointment_id: f.appointment_id, service_id: f.service_id, rule_id: f.rule_id }); toast.success("Relance envoyée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mt-6" data-testid="service-followups">
      <h3 className="font-heading text-base font-semibold text-slate-900 mb-1">Relances automatiques par prestation</h3>
      <p className="text-xs text-slate-500 mb-3">Configurées dans chaque prestation (Prestations → modifier → Relances automatiques). Envoi automatique à l'échéance, ou manuel ci-dessous.</p>
      {rows === null ? <Loader2 className="w-5 h-5 animate-spin text-emerald-600" /> : rows.length ? (
        <div className="space-y-2">
          {rows.map((f) => { const [l, c] = FU[f.status] || FU.planifiee; return (
            <div key={f.key} data-testid="followup-row" className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="min-w-0"><div className="text-sm font-medium text-slate-900 break-words">{f.client_name} — {f.label}</div><div className="text-xs text-slate-500">{f.service} · échéance {fmt(f.due_date)}</div></div>
              <div className="flex items-center gap-2"><span className={`text-[10px] px-2 py-0.5 rounded-full border ${c}`}>{l}</span>
                {["planifiee", "a_envoyer"].includes(f.status) && <Button size="sm" variant="outline" data-testid="followup-send" onClick={() => send(f)}><Mail className="w-3.5 h-3.5 mr-1" />Envoyer maintenant</Button>}</div>
            </div>); })}
        </div>
      ) : <p className="text-sm text-slate-500">Aucune relance programmée.</p>}
    </div>
  );
}
